import { useEffect, useRef, useState } from "react";

/**
 * Browser dictation (Web Speech API) — tap to talk, live transcript streams
 * into `onText(sessionTranscript)`. No server, no cost. `supported` is false
 * on browsers without speech recognition so the mic button can hide itself.
 */
export default function useSpeech(onText) {
  const SR =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const stop = () => {
    try {
      recRef.current?.stop();
    } catch (e) {
      /* already stopped */
    }
  };

  const toggle = () => {
    if (listening) {
      stop();
      return;
    }
    if (!SR) return;
    const rec = new SR();
    rec.lang = navigator.language || "en-US";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let transcript = "";
      for (let i = 0; i < e.results.length; i += 1) {
        transcript += e.results[i][0].transcript;
      }
      onTextRef.current(transcript.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (e) {
      setListening(false);
    }
  };

  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return { supported: !!SR, listening, toggle, stop };
}
